import hoverintent from 'hoverintent';
import isTouchDevice from '../../utils/isTouchDevice';

export default class HoverPanel {
	constructor() {
		this.hoverPanelSelector = '.hover-panel';
		this.hoverClass = 'hover-panel--active';
		this.hoverActiveOnLanding = 'hover-panel--active-on-landing';
		this.hoverElementSelector = '[data-hover-intent="true"]';
		this.cookieBannerID = '#onetrust-consent-sdk';
		this.clickElementSelector = '[data-click-event="true"]';
		this.contentSelector = '[data-panel-content]';
		this.isOpened = false;
		this.isTouchDevice = isTouchDevice();
		this.activeOnLandingAppearanceDelay = 1500;

		this.init();
	}

	attachHoverEvents() {
		if (!this.hoverEventsAreAttached) {
			this.hoverElements.forEach(el => {
				this.attachHoverEvent(el);
			});

			this.hoverEventsAreAttached = true;
		}
	}

	attachHoverEvent(el) {
		hoverintent(
			el,
			() => {
				this.closeAllPanels();
				this.checkAndTriggerAjaxEvent(el);
				this.toggleHoverClass(el);
			},
			() => this.toggleHoverClass(el),
		);
	}

	checkAndTriggerAjaxEvent(el) {
		const ajaxType = el.getAttribute('data-ajax');

		if (ajaxType) {
			const event = new Event(ajaxType);
			el.dispatchEvent(event);
		}
	}

	attachClickEvents() {
		if (!this.clickEventsAreAttached) {
			this.clickElements.forEach(el => {
				this.attachClickEvent(el);
			});

			this.closeOnClickOutside();

			this.clickEventsAreAttached = true;
		}
	}

	attachClickEvent(element) {
		element.addEventListener('click', event => {
			event.stopPropagation();

			const { target } = event;
			const isContentChild = target.closest(this.contentSelector);
			if (!isContentChild && !this.isTouchDevice) {
				event.preventDefault();
			}

			const isAnchorTag = element.querySelector('a.hover-panel__main-link');
			if (isAnchorTag && this.isTouchDevice) return;

			if (!element.classList.contains(this.hoverClass)) {
				this.closeAllPanels();
				this.toggleHoverClass(element);
				this.isOpened = true;
			} else {
				const isContent = target.hasAttribute(this.contentSelector);

				if (!isContent && !isContentChild) {
					this.toggleHoverClass(element);
					this.isOpened = false;
				}
			}

			this.checkAndTriggerAjaxEvent(element);
		});
	}

	closeOnClickOutside() {
		document.addEventListener('click', event => {
			this.isOpened = document.getElementsByClassName(this.hoverClass);

			if (this.isOpened) {
				const { target } = event;
				const isHoverPanel =
					target.getAttribute('data-click-event') === 'true' ||
					target.getAttribute('data-hover-intent') === 'true';
				const isChildElement =
					target.closest(this.clickElementSelector) ||
					target.closest(this.hoverElementSelector);
				const isCookieBanner = target.closest(this.cookieBannerID);

				if (!isHoverPanel && !isChildElement && !isCookieBanner)
					this.closeAllPanels();
			}
		});
	}

	closeAllPanels() {
		this.hoverPanelElements.forEach(el => {
			const openedElement = el.classList.contains(this.hoverClass);

			if (openedElement) this.toggleHoverClass(el);
			if (!openedElement && el.classList.contains(this.hoverActiveOnLanding))
				el.classList.remove(this.hoverActiveOnLanding);
		});

		this.setUserClosedHoverPanel(true);
	}

	toggleHoverClass(el) {
		el.classList.toggle(this.hoverClass);
	}

	attachEvents() {
		if (!this.isTouchDevice || (this.isTouchDevice && this.isMouseDetected)) {
			this.attachHoverEvents();
		} else {
			this.clickElements = [...this.hoverElements, ...this.clickElements];
		}
		this.attachClickEvents();
	}

	setUserClosedHoverPanel(closed) {
		if (closed === this.hasUserClosedPanel()) return;

		sessionStorage.setItem('userClosedHoverPanel', closed.toString());
	}

	hasUserClosedPanel() {
		return Boolean(sessionStorage.getItem('userClosedHoverPanel'));
	}

	closeActivePanelsIfDismissed() {
		if (!this.hasUserClosedPanel()) return;

		this.hoverPanelElements?.forEach(panel => {
			if (
				!panel?.classList?.contains(this.hoverClass) &&
				!panel?.classList?.contains(this.hoverActiveOnLanding)
			)
				return;

			panel?.classList?.remove(this.hoverClass);
			panel?.classList?.remove(this.hoverActiveOnLanding);
		});
	}

	handleHoverActiveOnLanding() {
		if (this.hasUserClosedPanel() || window?.innerWidth < 992) return;

		this.hoverPanelElements?.forEach(panel => {
			if (!panel?.classList?.contains(this.hoverActiveOnLanding)) return;

			setTimeout(() => {
				panel.classList.add(this.hoverClass);
			}, this.activeOnLandingAppearanceDelay);
		});
	}

	init() {
		this.hoverElements = document.querySelectorAll(this.hoverElementSelector);
		this.clickElements = document.querySelectorAll(this.clickElementSelector);
		this.hoverPanelElements = document.querySelectorAll(
			this.hoverPanelSelector,
		);

		this.isMouseDetected = false;

		this.handleHoverActiveOnLanding();
		// If a panel is active on landing for a new session and user closed it, it shouldn't reappear by default for the same user session
		this.closeActivePanelsIfDismissed();

		this.attachEvents();

		document.addEventListener('mousemove', () => {
			if (!this.isMouseDetected && !this.touchScreenIsUsed) {
				this.isMouseDetected = true;
				this.attachEvents();
			}
		});

		document.addEventListener('touchstart', () => {
			this.touchScreenIsUsed = true;
		});
	}
}
